// The graphite 3D stage shared by site scans (splats) and models/point clouds:
// near-full-bleed panes, drawing-style frame marks, an overlay toolbar with
// thin line icons, Bricks loader, per-pane errors, fullscreen (native where
// the browser allows it, CSS on iPhone Safari) and a date toggle on phones.
import { useCallback, useEffect, useRef, useState } from 'react';
import BricksLoader from '../BricksLoader';
import { Icon, IconButton, Segmented, STAGE, F, T } from './siteUi';
import { humanDate, shortHuman } from './siteTimelineModel';

export const GRID = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><path d='M96 0H0V96' fill='none' stroke='${STAGE.line}' stroke-width='1'/><path d='M48 0V96M0 48H96' fill='none' stroke='${STAGE.line}' stroke-width='1' stroke-dasharray='2 6'/></svg>`,
)}")`;

export function useIsPhone(query = '(max-width: 719px)') {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [is, setIs] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const m = window.matchMedia(query);
    const on = () => setIs(m.matches);
    on();
    if (m.addEventListener) m.addEventListener('change', on); else m.addListener(on);
    return () => { if (m.removeEventListener) m.removeEventListener('change', on); else m.removeListener(on); };
  }, [query]);
  return is;
}

// Stage size from its own width: side by side only when each date gets a
// real pane (>= 380px), otherwise one date at a time with a toggle.
export function useStageSize(ref) {
  const phone = useIsPhone();
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((ents) => setW(ents[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const height = phone ? 'min(68svh, 560px)' : `${Math.round(Math.max(420, Math.min(vh * 0.66, (w || 1100) * 0.62, 760)))}px`;
  return { phone, wide: w ? w >= 760 : !phone, height, minHeight: phone ? 380 : 420 };
}

// Corner registration marks, like the frame lines in a viewfinder.
export function FrameMarks() {
  const arm = 8;
  const at = (pos) => ({ position: 'absolute', width: arm, height: arm, ...pos });
  const line = (side) => ({ [side]: `1px solid ${STAGE.mark}` });
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 8, pointerEvents: 'none' }}>
      <span style={{ ...at({ left: 0, top: 0 }), ...line('borderLeft'), ...line('borderTop') }} />
      <span style={{ ...at({ right: 0, top: 0 }), ...line('borderRight'), ...line('borderTop') }} />
      <span style={{ ...at({ left: 0, bottom: 0 }), ...line('borderLeft'), ...line('borderBottom') }} />
      <span style={{ ...at({ right: 0, bottom: 0 }), ...line('borderRight'), ...line('borderBottom') }} />
    </div>
  );
}

function PaneLabel({ kindLabel, date, style }) {
  if (!date && !kindLabel) return null;
  return (
    <div style={{ position: 'absolute', left: 20, top: 20, display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 12px',
      background: STAGE.panel, border: `1px solid ${STAGE.hair}`, borderRadius: 2, pointerEvents: 'none', maxWidth: 'calc(100% - 40px)', ...style }}>
      {kindLabel ? <span style={{ fontFamily: F.display, fontSize: 12, color: STAGE.mute, whiteSpace: 'nowrap' }}>{kindLabel}</span> : null}
      {date ? <span style={{ fontFamily: F.display, fontSize: 15, fontWeight: 500, color: STAGE.ink, whiteSpace: 'nowrap' }}>{humanDate(date)}</span> : null}
    </div>
  );
}

function PaneState({ st, url }) {
  if (st.status === 'ready') return null;
  const err = st.status === 'error';
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
      padding: 24, textAlign: 'center', color: STAGE.ink, fontFamily: F.display, backgroundColor: STAGE.bg, backgroundImage: GRID,
      pointerEvents: err ? 'auto' : 'none' }}>
      {err ? (
        <>
          <Icon name="alert" size={28} color={STAGE.mute} />
          <span style={{ fontSize: 15, lineHeight: 1.5, maxWidth: 340 }}>{st.message}</span>
          <a href={url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 16px',
            border: `1px solid ${STAGE.hair}`, borderRadius: 2, color: STAGE.ink, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>
            <Icon name="download" size={18} />Download
          </a>
        </>
      ) : (
        <>
          <BricksLoader variant="assemble" size={40} color={T.brick} label="Loading" />
          <span aria-live="polite" style={{ fontSize: 13, color: STAGE.mute, fontVariantNumeric: 'tabular-nums' }}>{st.progress || 'Loading'}</span>
        </>
      )}
    </div>
  );
}

// items: [{ url, date?, kindLabel? }]; state: [{ status, message?, progress? }]
export default function StageFrame({ testid, paneTestid, items, state, paneRefs, onReset, onClose, onShown, hint, title }) {
  const host = useRef(null);
  const size = useStageSize(host);
  const pair = items.length > 1;
  const split = pair && size.wide;
  const toggle = pair && !size.wide;
  const [active, setActive] = useState(items.length - 1);
  const [cssFull, setCssFull] = useState(false);
  const [nativeFull, setNativeFull] = useState(false);
  const [hintOn, setHintOn] = useState(true);
  const full = cssFull || nativeFull;

  // Tell the renderer which panes are on show (a phone shows one date at a time).
  const count = items.length;
  useEffect(() => {
    if (onShown) onShown(Array.from({ length: count }, (_, i) => !toggle || i === active));
  }, [onShown, toggle, active, count]);

  useEffect(() => {
    const on = () => setNativeFull(Boolean(document.fullscreenElement || document.webkitFullscreenElement) && (document.fullscreenElement || document.webkitFullscreenElement) === host.current);
    document.addEventListener('fullscreenchange', on);
    document.addEventListener('webkitfullscreenchange', on);
    return () => { document.removeEventListener('fullscreenchange', on); document.removeEventListener('webkitfullscreenchange', on); };
  }, []);
  useEffect(() => {
    if (!cssFull) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setCssFull(false); };
    window.addEventListener('keydown', esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', esc); document.body.style.overflow = prev; };
  }, [cssFull]);
  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    const off = () => setHintOn(false);
    el.addEventListener('pointerdown', off, { once: true });
    return () => el.removeEventListener('pointerdown', off);
  }, []);

  const toggleFull = useCallback(() => {
    const doc = document;
    if (doc.fullscreenElement || doc.webkitFullscreenElement) { (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc); return; }
    if (cssFull) { setCssFull(false); return; }
    const el = host.current;
    const req = el && (el.requestFullscreen || el.webkitRequestFullscreen);
    if (req) Promise.resolve(req.call(el)).catch(() => setCssFull(true));
    else setCssFull(true);
  }, [cssFull]);

  const outer = cssFull
    ? { position: 'fixed', inset: 0, zIndex: 2147483000 }
    : { position: 'relative', width: '100%', height: size.height, borderRadius: 2 };

  return (
    <section ref={host} data-testid={testid} aria-label={title || '3D view'}
      style={{ ...outer, minHeight: size.minHeight, overflow: 'hidden', background: STAGE.bg, color: STAGE.ink, fontFamily: F.display, boxSizing: 'border-box' }}>
      <div style={{ position: 'absolute', inset: 0, display: split ? 'grid' : 'block', gridTemplateColumns: split ? '1fr 1fr' : undefined, gap: split ? 1 : 0, background: split ? STAGE.lineS : 'transparent' }}>
        {items.map((it, i) => {
          const st = state[i] || {};
          const hidden = toggle && i !== active;
          return (
            <div key={it.url} data-testid={`${paneTestid}${i}`} aria-hidden={hidden || undefined}
              style={{ position: split ? 'relative' : 'absolute', inset: split ? undefined : 0, minWidth: 0, overflow: 'hidden', background: STAGE.bg,
                opacity: hidden ? 0 : 1, pointerEvents: hidden ? 'none' : 'auto', zIndex: hidden ? 0 : 1 }}>
              <div ref={(el) => { paneRefs.current[i] = el; }} style={{ position: 'absolute', inset: 0 }} />
              <PaneState st={st} url={it.url} />
              <FrameMarks />
              <PaneLabel kindLabel={it.kindLabel} date={it.date} style={size.phone ? { top: 76 } : undefined} />
            </div>
          );
        })}
      </div>

      <div style={{ position: 'absolute', right: 20, top: 20, zIndex: 3, display: 'flex', gap: 8 }}>
        <IconButton icon="reset" label="Reset view" onClick={() => onReset && onReset(toggle ? active : 0)} />
        <IconButton icon={full ? 'unfull' : 'full'} label={full ? 'Exit full screen' : 'Full screen'} onClick={toggleFull} />
        {!pair ? <IconButton icon="download" label="Download" href={items[0].url} /> : null}
        {onClose ? <IconButton icon="close" label="Close" onClick={() => { if (cssFull) setCssFull(false); onClose(); }} /> : null}
      </div>

      {toggle ? (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 20, zIndex: 3, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ pointerEvents: 'auto' }}>
            <Segmented stage label="Show date" value={active} onChange={setActive}
              options={items.map((it, i) => ({ value: i, label: shortHuman(it.date) || `View ${i + 1}` }))} />
          </div>
        </div>
      ) : null}

      <p aria-live="polite" style={{ position: 'absolute', left: 20, bottom: toggle ? 76 : 20, zIndex: 2, margin: 0, padding: '6px 10px', maxWidth: 'calc(100% - 40px)',
        fontSize: 12.5, color: STAGE.mute, background: STAGE.panel, border: `1px solid ${STAGE.hair}`, borderRadius: 2, pointerEvents: 'none',
        opacity: hintOn ? 1 : 0, transition: 'opacity 400ms ease' }}>
        {hint}
      </p>
    </section>
  );
}
